import React, { useEffect, useState } from "react";
import StoreLogo from "@/components/storefront/StoreLogo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, X, ChevronUp, MapPin, Mail, Truck, ShieldCheck, Sprout, Flower2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/pharmacy/hooks";
import ToneImg from "@/components/storefront/ToneImg";
import MobileNav from "@/components/storefront/MobileNav";
import { socialItems } from "@/lib/social";
import { hasPriceRange } from "@/lib/variants";
import { inr } from "@/lib/storeutils";
import { navLinks, footerColumns, footerLines, trustItems, isNavActive } from "@/lib/storenav";

// Look-and-feel tokens of the "Aroma Soap" layout: airy white and warm cream, near-black text, a terracotta accent,
// small letter-spaced labels and a light serif for headlines.
export const A = {
  ink: "var(--a-ink, #2b2420)", accent: "var(--a-accent, #c4793f)", cream: "#f6f1ea", sand: "#efe7db", line: "#e8e0d4", muted: "#8a7f74",
  serif: "'Marcellus', Georgia, serif",
};
export const label = { letterSpacing: ".28em" };

export const pct = (p) => (p.compare_at_price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0);
export const needsOptions = (p) => p.variants?.length > 0 || p.variant_items?.length > 0;

/** Section heading: tiny spaced eyebrow over a light serif title, centred. */
export function ATitle({ eyebrow, children, className = "" }) {
  return (
    <div className={`text-center ${className}`}>
      {eyebrow && <div className="text-[11px] uppercase" style={{ ...label, color: A.muted }}>{eyebrow}</div>}
      <h2 className="mt-2.5 text-[28px] md:text-[34px] leading-tight" style={{ fontFamily: A.serif, color: A.ink }}>{children}</h2>
    </div>
  );
}

/** Square-cornered outline / solid button with a sliding fill on hover. */
export function AButton({ to, onClick, children, solid = false, className = "", testId }) {
  const cls = `aroma-btn ${solid ? "aroma-btn-solid" : ""} inline-flex items-center justify-center h-10 px-7 text-[11px] font-medium uppercase ${className}`;
  const st = { letterSpacing: ".2em", border: `1px solid ${A.ink}`, color: solid ? "#fff" : A.ink, background: solid ? A.ink : "transparent" };
  return to ? <Link to={to} className={cls} style={st} data-testid={testId}>{children}</Link> : <button type="button" onClick={onClick} className={cls} style={st} data-testid={testId}>{children}</button>;
}

export function AromaHeader({ s, cart }) {
  const { store, slug } = s;
  const nav = useNavigate();
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const [stuck, setStuck] = useState(false);
  useEffect(() => { const f = () => setStuck(window.scrollY > 40); f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const search = (e) => { e.preventDefault(); setSearching(false); nav(`/store/${slug}/shop${q ? `?q=${encodeURIComponent(q)}` : ""}`); };
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-30 bg-white transition-shadow" style={{ boxShadow: stuck ? "0 6px 24px rgba(43,36,32,.08)" : "none", borderBottom: `1px solid ${stuck ? "transparent" : A.line}` }}>
      <div className="max-w-6xl mx-auto px-5 h-[72px] flex items-center justify-between gap-6">
        <Link to={`/store/${slug}`} data-testid="storefront-name" className="shrink-0 flex items-center gap-2.5 truncate max-w-[300px]">
          <StoreLogo store={store} height={42} fallback={<span className="text-[22px] uppercase truncate" style={{ fontFamily: A.serif, letterSpacing: ".14em", color: A.ink }}>{store.name}</span>} />
        </Link>
        <nav className="hidden lg:flex items-center gap-9 text-[11px] font-medium uppercase" style={{ letterSpacing: ".16em" }} data-testid="storefront-nav">
          {navLinks(slug).map(([l, to]) => { const on = isNavActive(pathname, slug, to); return (
            <Link key={l} to={to} aria-current={on ? "page" : undefined} className="rx-link py-1 transition-colors hover:opacity-70" style={{ color: on ? A.accent : A.ink }} data-testid={l === "Track Order" ? "storefront-track-link" : undefined}>{l}</Link>
          ); })}
        </nav>
        <div className="flex items-center gap-4" style={{ color: A.ink }}>
          <button onClick={() => setSearching((x) => !x)} aria-label="Search" data-testid="aroma-search-toggle" className="hover:opacity-60 transition-opacity"><Search size={17} /></button>
          <Link to={`/store/${slug}/track`} aria-label="Track order" className="hidden sm:block hover:opacity-60 transition-opacity"><User size={17} /></Link>
          <button onClick={() => cart.setOpen(true)} data-testid="storefront-cart-btn" aria-label="Open cart" className="relative hover:opacity-60 transition-opacity">
            <ShoppingBag size={18} />
            <span className="absolute -top-2 -right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center" style={{ background: A.accent, color: "#fff" }}>{cart.count}</span>
          </button>
          <MobileNav slug={slug} color={A.ink} bg="#fff" accent={A.accent} border={A.line} />
        </div>
      </div>
      {searching && (
        <form onSubmit={search} className="border-t rx-fade" style={{ borderColor: A.line, background: A.cream }}>
          <div className="max-w-6xl mx-auto px-5 h-14 flex items-center gap-3">
            <Search size={16} style={{ color: A.muted }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search soaps, skincare…" aria-label="Search products" data-testid="aroma-search" className="flex-1 outline-none text-[14px] bg-transparent" style={{ color: A.ink }} />
            <button type="button" onClick={() => setSearching(false)} aria-label="Close search"><X size={16} /></button>
          </div>
        </form>
      )}
    </header>
  );
}

export function AromaFooter({ s }) {
  const { store, slug } = s;
  const [top, setTop] = useState(false);
  useEffect(() => { const f = () => setTop(window.scrollY > 600); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const lines = footerLines(store);
  const icons = { money: Sprout, verified: ShieldCheck, delivery: Truck };
  // Same structure as every theme: trust strip, store details + the three standard link columns, copyright.
  return (
    <footer data-testid="storefront-footer" style={{ background: A.cream, color: A.ink, borderTop: `1px solid ${A.line}` }}>
      <div style={{ borderBottom: `1px solid ${A.line}` }}>
        <div className="max-w-6xl mx-auto px-5 py-8 grid sm:grid-cols-3 gap-6">
          {trustItems(store.requires_prescription, store.free_shipping_over).map(([key, h, d], k) => { const I = icons[key]; return (
            <Reveal key={h} delay={k * 100} className="flex gap-4 items-center sm:justify-center">
              <span className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-white" style={{ color: A.accent, border: `1px solid ${A.line}` }}><I size={20} /></span>
              <div><div className="text-[15px]" style={{ fontFamily: A.serif }}>{h}</div><p className="text-[12px] mt-0.5" style={{ color: A.muted }}>{d}</p></div>
            </Reveal>
          ); })}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 py-12 grid md:grid-cols-5 gap-9">
        <Reveal className="md:col-span-2">
          <StoreLogo store={store} height={46} fallback={<div className="text-[22px] uppercase" style={{ fontFamily: A.serif, letterSpacing: ".14em" }}>{store.name}</div>} />
          <p className="text-[13px] mt-4 flex gap-3" style={{ color: A.muted }}><MapPin size={16} className="mt-0.5 shrink-0" style={{ color: A.accent }} />{lines.location}</p>
          <p className="text-[13px] mt-2 flex gap-3" style={{ color: A.muted }}><Mail size={16} className="mt-0.5 shrink-0" style={{ color: A.accent }} />{lines.support}</p>
          <div className="mt-4 flex gap-2.5">{socialItems(s).map((I) => <a key={I.network} href={I.url} target="_blank" rel="noopener noreferrer" aria-label={I.label} title={I.label} data-testid={`footer-social-${I.network}`}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-white transition-colors hover:text-white" style={{ border: `1px solid ${A.line}`, color: A.ink }}
            onMouseEnter={(e) => { e.currentTarget.style.background = A.ink; }} onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}><I.icon size={14} /></a>)}</div>
        </Reveal>
        {footerColumns(slug).map(([h, links], i) => (
          <Reveal key={h} delay={(i + 1) * 90}>
            <div className="text-[11px] uppercase pb-3 border-b" style={{ ...label, borderColor: A.line }}>{h}</div>
            <ul className="mt-5 space-y-3 text-[13px]" style={{ color: A.muted }}>{links.map(([l, to]) => <li key={l}><Link to={to} className="rx-link hover:opacity-100" style={{ color: "inherit" }}>{l}</Link></li>)}</ul>
          </Reveal>
        ))}
      </div>
      <div className="border-t text-center text-[12px] py-5" style={{ borderColor: A.line, color: A.muted }}>© 2026 {store.name} · Powered by <Link to="/" className="underline">Sell24x7</Link></div>
      {top && <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" className="fixed bottom-6 right-6 z-30 w-10 h-10 rounded-full flex items-center justify-center rx-slide-up" style={{ background: A.ink, color: "#fff" }}><ChevronUp size={18} /></button>}
    </footer>
  );
}

/** Product card of the Aroma layout: square photo, small spaced category, centred name and price; Add button slides up on hover. */
export function AromaCard({ p, s, cart }) {
  const { slug } = s;
  const off = pct(p);
  const hasVariants = needsOptions(p);
  const soldOut = p.stock <= 0;
  const [added, setAdded] = useState(false);
  const add = (e) => {
    e.preventDefault();
    if (hasVariants) { window.location.href = `/store/${slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1200);
  };
  return (
    <div data-testid={`storefront-product-${p.id}`} className="group h-full flex flex-col bg-white" style={{ border: `1px solid ${A.line}` }}>
      <Link to={`/store/${slug}/product/${p.id}`} className="relative block overflow-hidden aspect-square" style={{ background: A.sand }}>
        {p.image ? <ToneImg src={p.image} alt={p.name} loading="lazy" decoding="async" imgClassName="transition-transform duration-700 group-hover:scale-105" /> : <Flower2 size={36} className="m-auto mt-[40%]" style={{ color: A.muted }} />}
        {(off > 0 || soldOut) && <span className="absolute top-3 left-3 text-[10px] font-medium px-2 py-1 text-white" style={{ background: soldOut ? "#888" : "#d9534f", letterSpacing: ".06em" }}>{soldOut ? "SOLD OUT" : `-${off}%`}</span>}
        <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} aria-label={hasVariants ? "Select options" : "Add to cart"}
          className="absolute left-0 right-0 bottom-0 h-10 text-[11px] font-medium uppercase text-white flex items-center justify-center gap-2 translate-y-full group-hover:translate-y-0 focus-visible:translate-y-0 transition-transform duration-300 disabled:opacity-60" style={{ background: A.ink, letterSpacing: ".18em" }}>
          <ShoppingBag size={13} />{added ? "Added" : hasVariants ? "Select options" : "Add to cart"}
        </button>
      </Link>
      <div className="px-3 pt-3.5 pb-4 text-center flex flex-col flex-1">
        {p.category && <div className="text-[9.5px] uppercase truncate" style={{ letterSpacing: ".2em", color: A.muted }}>{p.category}</div>}
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className="mt-1.5 text-[13px] leading-snug line-clamp-2 min-h-[2.6em]" style={{ color: A.ink }}>{p.name}</h3></Link>
        <div className="mt-auto pt-2 flex items-baseline justify-center gap-2">
          {hasPriceRange(p) && <span className="text-[11px]" style={{ color: A.muted }}>From</span>}
          <span className="text-[13px] font-semibold" style={{ color: A.ink }}>{inr(p.price)}</span>
          {p.compare_at_price ? <span className="text-[12px] line-through" style={{ color: A.muted }}>{inr(p.compare_at_price)}</span> : null}
        </div>
      </div>
    </div>
  );
}

